import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { handlePossibleNonFieldError } from 'api/query-client';

import {
  createEmailTrigger,
  deleteEmailTrigger,
  generateInstructions,
  GenerateInstructionsPayload,
  JourneyEmailTrigger,
  JourneyEmailTriggerData,
  JourneyEmailTriggerList,
  loadEmailTrigger,
  loadEmailTriggerLog,
  loadEmailTriggerLogs,
  loadEmailTriggers,
  updateEmailTrigger,
} from './requests';

const getEmailTriggersKey = (journeyShortId: string) => {
  return ['journey', journeyShortId, 'email-triggers'];
};

const getEmailTriggerKey = (shortId: string) => {
  return ['journey', shortId, 'email-trigger'];
};

const useInvalidateEmailTriggers = () => {
  const queryClient = useQueryClient();

  const invalidateEmailTriggers = (journeyShortId: string) => {
    return queryClient.invalidateQueries(getEmailTriggersKey(journeyShortId));
  };

  const invalidateEmailTrigger = (shortId: string) => {
    return queryClient.invalidateQueries(getEmailTriggerKey(shortId));
  };

  return { invalidateEmailTriggers, invalidateEmailTrigger };
};

export const useEmailTriggersList = (journeyShortId: string) => {
  return useQuery({
    queryKey: getEmailTriggersKey(journeyShortId),
    queryFn: () => {
      return loadEmailTriggers(journeyShortId);
    },
  });
};

export const useEmailTrigger = (shortId: string) => {
  return useQuery({
    queryKey: getEmailTriggerKey(shortId),
    queryFn: () => {
      return loadEmailTrigger(shortId);
    },
  });
};

export const useCreateEmailTrigger = (journeyShortId: string) => {
  const { invalidateEmailTriggers } = useInvalidateEmailTriggers();
  return useMutation({
    mutationFn: () => {
      return createEmailTrigger(journeyShortId);
    },
    onSuccess: () => {
      invalidateEmailTriggers(journeyShortId);
    },
  });
};

export const useUpdateEmailTrigger = (trigger: JourneyEmailTrigger | JourneyEmailTriggerList) => {
  const { invalidateEmailTriggers, invalidateEmailTrigger } = useInvalidateEmailTriggers();
  return useMutation({
    mutationFn: (data: JourneyEmailTriggerData) => {
      return updateEmailTrigger(trigger.shortId, data);
    },
    onSuccess: () => {
      invalidateEmailTrigger(trigger.shortId);
      invalidateEmailTriggers(trigger.journeyId);
    },
  });
};

export const useDeleteEmailTrigger = (journeyShortId: string) => {
  const { invalidateEmailTriggers } = useInvalidateEmailTriggers();
  return useMutation({
    mutationFn: (shortId: string) => {
      return deleteEmailTrigger(shortId);
    },
    onSuccess: () => {
      invalidateEmailTriggers(journeyShortId);
    },
  });
};

export const useGenerateInstructions = (triggerShortId: string) => {
  const { t } = useScopedTranslate(locale);
  return useMutation({
    mutationFn: (payload: GenerateInstructionsPayload) => {
      return generateInstructions(triggerShortId, payload);
    },
    onError: (error: unknown) => {
      handlePossibleNonFieldError(error, t('generateInstructionsFailed'));
    },
  });
};

const locale = {
  en: {
    generateInstructionsFailed: 'Failed to generate field instructions',
  },
};

export const useEmailTriggerLogs = (triggerShortId: string) => {
  return useQuery({
    queryKey: ['journeys', triggerShortId, 'email-trigger', 'logs'],
    queryFn: () => {
      return loadEmailTriggerLogs(triggerShortId);
    },
  });
};

export const useEmailTriggerLog = (triggerShortId: string, logShortId: string) => {
  return useQuery({
    queryKey: ['journeys', triggerShortId, logShortId, 'email-trigger', 'log'],
    queryFn: () => {
      return loadEmailTriggerLog(triggerShortId, logShortId);
    },
  });
};
